/** Colour helpers shared by the sun animations (hero load rise, services scrub). */

function lerp(from: number, to: number, t: number) {
  return from + (to - from) * t
}

function parseHexColor(hex: string): [number, number, number] | null {
  const normalized = hex.trim().replace(/^#/, '')
  if (normalized.length === 3) {
    const r = Number.parseInt(normalized[0] + normalized[0], 16)
    const g = Number.parseInt(normalized[1] + normalized[1], 16)
    const b = Number.parseInt(normalized[2] + normalized[2], 16)
    return [r, g, b]
  }
  if (normalized.length === 6) {
    const r = Number.parseInt(normalized.slice(0, 2), 16)
    const g = Number.parseInt(normalized.slice(2, 4), 16)
    const b = Number.parseInt(normalized.slice(4, 6), 16)
    return [r, g, b]
  }
  return null
}

export function mixHexColors(from: string, to: string, t: number): string {
  const a = parseHexColor(from)
  const b = parseHexColor(to)
  if (!a || !b) return t < 0.5 ? from : to
  const r = Math.round(lerp(a[0], b[0], t))
  const g = Math.round(lerp(a[1], b[1], t))
  const bl = Math.round(lerp(a[2], b[2], t))
  return `rgb(${r} ${g} ${bl})`
}

/** Reads a hex colour from a custom property on `:root`. */
export function readCssColor(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim()
  return value || fallback
}

function rgbChannelsToHex(r: number, g: number, b: number): string | null {
  if (![r, g, b].every((n) => Number.isFinite(n))) return null
  return `#${[r, g, b]
    .map((n) => Math.round(n).toString(16).padStart(2, '0'))
    .join('')}`
}

/**
 * Resolves a CSS colour (hex, `color-mix`, oklab, rgb, etc.) to `#rrggbb`.
 * Uses a canvas sample so modern computed formats (e.g. `oklab(...)`) work.
 * Falls back when the value cannot be painted/parsed.
 */
export function resolveCssColorToHex(color: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback

  const probe = document.createElement('span')
  probe.style.backgroundColor = color
  probe.style.position = 'absolute'
  probe.style.visibility = 'hidden'
  probe.style.pointerEvents = 'none'
  document.documentElement.appendChild(probe)
  const resolved = getComputedStyle(probe).backgroundColor
  probe.remove()

  const match = resolved.match(
    /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i,
  )
  if (match) {
    const hex = rgbChannelsToHex(
      Number(match[1]),
      Number(match[2]),
      Number(match[3]),
    )
    if (hex) return hex
  }

  // Safari/Chromium may report `oklab(...)` — sample via canvas to get sRGB.
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    const ctx = canvas.getContext('2d')
    if (!ctx) return fallback
    ctx.fillStyle = resolved || color
    ctx.fillRect(0, 0, 1, 1)
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data
    return rgbChannelsToHex(r, g, b) ?? fallback
  } catch {
    return fallback
  }
}

/** Reads a custom property and resolves it to `#rrggbb` (handles `color-mix`). */
export function readResolvedCssColor(name: string, fallback: string): string {
  return resolveCssColorToHex(`var(${name})`, fallback)
}
